import React from 'react';
import { useAICoach } from '../contexts/AICoachContext';
import { AlertTriangle, TrendingUp, Target, Lightbulb, CheckCircle, AlertCircle, Info } from 'lucide-react';

export const AICoachInsights: React.FC = () => {
  const { context, loading, urgentAlerts, motivationLevel, hasStagnation } = useAICoach();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading insights...</span>
        </div>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No coaching insights available</p>
      </div>
    );
  }

  const getMotivationColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      default:
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">AI Career Coach Insights</h2>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMotivationColor(motivationLevel)}`}>
          {motivationLevel.charAt(0).toUpperCase() + motivationLevel.slice(1)} Motivation
        </span>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Active Skills</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{context.skills.activeSkills}</p>
          <p className="text-xs text-blue-700 mt-1">{context.skills.totalSkills} total</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Avg Progress</span>
          </div>
          <p className="text-2xl font-bold text-green-900">{context.skills.averageProgress}%</p>
          <p className="text-xs text-green-700 mt-1">Across all skills</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Completed</span>
          </div>
          <p className="text-2xl font-bold text-purple-900">{context.skills.completedSkills}</p>
          <p className="text-xs text-purple-700 mt-1">Skills mastered</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-orange-900">Hours Invested</span>
          </div>
          <p className="text-2xl font-bold text-orange-900">{context.skills.totalHoursInvested}</p>
          <p className="text-xs text-orange-700 mt-1">Learning time</p>
        </div>
      </div>

      {/* Urgent Alerts */}
      {urgentAlerts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Attention Needed
          </h3>
          <div className="space-y-4">
            {urgentAlerts.map(alert => (
              <div
                key={alert.id}
                className={`border-l-4 p-4 rounded-r-lg ${
                  alert.severity === 'critical'
                    ? 'border-red-500 bg-red-50'
                    : alert.severity === 'warning'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(alert.severity)}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{alert.title}</h4>
                    <p className="text-sm text-gray-700 mb-3">{alert.message}</p>
                    {alert.actionable && alert.suggestedActions && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-900">Suggested Actions:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {alert.suggestedActions.map((action, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-gray-400">•</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Focus */}
      {context.insights.currentFocus.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Current Focus Areas
          </h3>
          <div className="flex flex-wrap gap-2">
            {context.insights.currentFocus.map((focus, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {focus}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Priority Skills */}
      {context.skills.prioritySkills.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Skills Development</h3>
          <div className="space-y-3">
            {context.skills.prioritySkills.slice(0, 5).map(skill => (
              <div key={skill.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{skill.skillName}</h4>
                    {skill.isStagnant && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">Stagnant</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{skill.currentLevel} → {skill.targetLevel}</span>
                    <span>•</span>
                    <span>{skill.hoursInvested}h invested</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{skill.progress}%</p>
                    <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                      <div
                        className="h-2 bg-blue-600 rounded-full transition-all"
                        style={{ width: `${skill.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {context.skills.recentActivity.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {context.skills.recentActivity.slice(0, 5).map((activity, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.skillName}</p>
                  <p className="text-xs text-gray-500">{activity.activityType}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-blue-600">+{activity.progressIncrement}%</p>
                  <p className="text-xs text-gray-500">{activity.hours}h</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Career Profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Career Phase</p>
            <p className="font-medium text-gray-900 capitalize">{context.profile.careerPhase}</p>
          </div>
          {context.profile.currentRole && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Role</p>
              <p className="font-medium text-gray-900">{context.profile.currentRole}</p>
            </div>
          )}
          {context.profile.yearsExperience && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Experience</p>
              <p className="font-medium text-gray-900">{context.profile.yearsExperience} years</p>
            </div>
          )}
          {context.profile.industry && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Industry</p>
              <p className="font-medium text-gray-900">{context.profile.industry}</p>
            </div>
          )}
        </div>
      </div>

      {/* Context Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p>
          Last updated: {new Date(context.lastUpdated).toLocaleString()} •
          Context version: {context.contextVersion}
        </p>
      </div>
    </div>
  );
};
